import React, {
	Fragment,
	useCallback,
	useMemo,
	useRef,
	useState,
	type PropsWithChildren,
} from 'react';
import noop from 'lodash/noop';

// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { useFragment, graphql, useMutation } from 'react-relay';
import { ModalTransition } from '@atlaskit/modal-dialog';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import type { OnSubmitCallbacks } from '@atlassian/jira-issue-field-inline-edit-actions/src/controllers/types.tsx';
import { useFieldInlineEditActions } from '@atlassian/jira-issue-field-inline-edit-actions/src/controllers/index.tsx';
import type { ValidationFieldProps } from '@atlassian/jira-issue-field-inline-edit-lite/src/ui/field-inline-edit-lite/types.tsx';
import { FieldInlineEditLiteWithEntryPoint } from '@atlassian/jira-issue-field-inline-edit-lite/src/ui/index.tsx';
import RelayDataID from '@atlassian/relay-data-id';
import { useFlagService } from '@atlassian/jira-flags';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';

import { useOrgId } from '@atlassian/jira-router-resources-navigation-org-id/src/index.tsx';
import { projectPickerEntrypoint as AtlasProjectEditViewEntrypoint } from '@atlassian/project-picker/projectPickerEntrypoint';
import type { ProjectPickerWithEntrypointProps } from '@atlassian/project-picker/ProjectPicker';
import {
	AtlasProjectReadView,
	AtlasProjectEmptytView,
} from '@atlassian/jira-issue-field-atlas-project-readview-full/src/ui/atlas-project/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import { useInlineEditFieldInjections } from '@atlassian/jira-issue-field-injections/src/controllers/inline-edit-injections-context/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { RestrictedAlikeSmartLink } from '@atlassian/jira-issue-field-atlas-project-common/src/ui/restricted/index.tsx';
import type { atlasProject_issueFieldAtlasProjectInlineEditFull_AtlasProjectInlineEditView_fragmentRef$key as AtlasProjectFragment } from './__generated__/atlasProject_issueFieldAtlasProjectInlineEditFull_AtlasProjectInlineEditView_fragmentRef.graphql';
import type { atlasProject_issueFieldAtlasProjectInlineEditFull_AtlasProjectInlineEditViewWithIsEditable_fragmentRef$key as AtlasProjectWithIsEditableFragment } from './__generated__/atlasProject_issueFieldAtlasProjectInlineEditFull_AtlasProjectInlineEditViewWithIsEditable_fragmentRef.graphql';
import type { atlasProject_issueFieldAtlasProject_AtlasProjectField_LinkProjectToWorkItemMutation as LinkProjectToWorkItemMutation } from './__generated__/atlasProject_issueFieldAtlasProject_AtlasProjectField_LinkProjectToWorkItemMutation.graphql';
import type { atlasProject_issueFieldAtlasProject_AtlasProjectField_UnlinkProjectFromWorkItemMutation as UnlinkProjectFromWorkItemMutation } from './__generated__/atlasProject_issueFieldAtlasProject_AtlasProjectField_UnlinkProjectFromWorkItemMutation.graphql';
import {
	type AtlasProjectInlineEditViewProps,
	type AtlasProjectInlineEditViewWithIsEditableProps,
	type LinkedProject,
	LINK_MUTATION_ERROR_TYPES,
	type HandleMutationErrorProps,
	type UpdaterProps,
} from './types.tsx';
import messages from './messages.tsx';
import { LINK_PROJECTS_TO_WORK_ITEM_HELP_ARTICLE_URL } from './constants.tsx';
import { LinkConfirmationModal } from './link-confirmation-modal/LinkConfirmationModal.tsx';
import { useAtlasProjectAnalyticsEvent } from './analytics.tsx';

/** Determine if the updated edit view value is equal to our original value before submitting a mutation. */
const isEqualOption = (a: LinkedProject | null, b: LinkedProject | null) => a?.id === b?.id;

const MaybeUFOSegment = componentWithFG(
	'nin_blindspot_fixes_fy26',
	({ children }: PropsWithChildren<{}>) => (
		<UFOSegment name="issue-field-atlas-project-read-view" mode="list">
			{children}
		</UFOSegment>
	),
	Fragment,
);

/**
 * Inline edit will handle the switching behaviour between the 'readView' and 'editView' components. This variant allows
 * consumers to define their own value to determine whether the field is editable.
 *
 * In most cases consumers should use `AtlasProjectInlineEditView` which will enforce that the user has permission to
 * edit the field within the issue view. However, this component can be used for experiences that have differing
 * permissions or want finer control for how this data is retrieved, e.g. lazy loading editability.
 *
 * @param props [AtlasProjectInlineEditViewWithIsEditableProps](./types.tsx)
 */
export const AtlasProjectInlineEditViewWithIsEditable = ({
	attributes,
	editViewPopup,
	editViewPopupAlignBlock,
	fragmentRef,
	isEditable,
	isEditing: startWithEditViewOpen = false,
	onSubmit,
	onSubmitFailed,
	onSubmitSucceeded,
	readViewFitContainerHeight,
	popupPlacement = 'bottom-end',
	spacing = 'compact',
}: AtlasProjectInlineEditViewWithIsEditableProps): React.JSX.Element => {
	// #region Relay
	const data = useFragment<AtlasProjectWithIsEditableFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment atlasProject_issueFieldAtlasProjectInlineEditFull_AtlasProjectInlineEditViewWithIsEditable_fragmentRef on JiraTownsquareProjectField {
				id
				name
				type
				issue {
					id
				}
				isLinkEditable
				project {
					id
					atlasProjectName: name
					atlasProjectKey: key
					privateProject
					hasPermission
					isWorkspaceActive
				}
				...atlasProject_issueFieldAtlasProjectReadviewFull_AtlasProjectReadView
			}
		`,
		fragmentRef,
	);

	// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
	const [commitLinkMutation] = useMutation<LinkProjectToWorkItemMutation>(graphql`
		mutation atlasProject_issueFieldAtlasProject_AtlasProjectField_LinkProjectToWorkItemMutation(
			$input: TownsquareProjectsAddJiraWorkItemLinkInput!
		) {
			projects_addJiraWorkItemLink(input: $input) {
				success
				errors {
					message
					extensions {
						errorType
						statusCode
						__typename
						... on TownsquareProjectsChildWorkItemsAlreadyLinkedMutationErrorExtension {
							canReplace
						}
					}
				}
			}
		}
	`);

	// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
	const [commitUnlinkMutation] = useMutation<UnlinkProjectFromWorkItemMutation>(graphql`
		mutation atlasProject_issueFieldAtlasProject_AtlasProjectField_UnlinkProjectFromWorkItemMutation(
			$input: TownsquareProjectsRemoveJiraWorkItemLinkInput!
		) {
			projects_removeJiraWorkItemLink(input: $input) {
				success
				errors {
					message
					extensions {
						errorType
						statusCode
					}
				}
			}
		}
	`);
	// #endregion Relay

	// #region Common state
	const { id: uniqueFieldId, name, type: fieldType, project, issue, isLinkEditable } = data;

	const initialValue = useMemo(
		() =>
			project
				? {
						id: project.id,
						name: project.atlasProjectName ?? '',
						key: project.atlasProjectKey ?? '',
						isPrivate: !!project.privateProject,
					}
				: null,
		[project],
	);
	const [updatedValue, setUpdatedValue] = useState<LinkedProject | null>(initialValue);
	const hasNoInitialAccess =
		project?.hasPermission === false || project?.isWorkspaceActive === false;
	const [hasNoAccess, setHasNoAccess] = useState(hasNoInitialAccess);
	const { formatMessage } = useIntl();
	const { overriding } = useInlineEditFieldInjections();
	const { overrideLabel, overrideIsEditable } = overriding;

	const fieldName = useMemo(() => overrideLabel(name), [overrideLabel, name]);

	const isRestricted = hasNoAccess && !!project?.privateProject && !hasNoInitialAccess;

	const isFieldEditable = useMemo(
		() => overrideIsEditable(isEditable && isLinkEditable !== false && !hasNoInitialAccess),
		[overrideIsEditable, isEditable, isLinkEditable, hasNoInitialAccess],
	);

	const handleNoAccess = useCallback(() => {
		setHasNoAccess(() => true);
	}, []);
	// #endregion Common state

	// #region Mutation handlers
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { fireAtlasProjectAnalyticsEvent } = useAtlasProjectAnalyticsEvent();
	const { showFlag } = useFlagService();
	const [showConfirmatonModal, setShowConfirmationModal] = useState(false);
	const replaceChildLinks = useRef(false);

	const handleMutationError = useCallback(
		({ errorType, canReplace, onFail, onFireAnalyticsEvent }: HandleMutationErrorProps) => {
			if (errorType === LINK_MUTATION_ERROR_TYPES.CHILD_ITEMS_LINKED_TO_ANOTHER_PROJECT) {
				if (canReplace) {
					setShowConfirmationModal(true);
				} else {
					showFlag({
						type: 'error',
						title: formatMessage(messages.childIssueAlreadyLinkedNoReplaceTitle),
						description: formatMessage(messages.childIssueAlreadyLinkedNoReplaceDescription),
						isAutoDismiss: false,
						messageId:
							'issue-field-atlas-project-inline-edit-full.ui.atlas-project.show-flag.error.child-issue-already-linked-to-another-project',
						messageType: 'transactional',
						actions: [
							{
								content: formatMessage(messages.childIssueAlreadyLinkedNoReplaceAction),
								href: LINK_PROJECTS_TO_WORK_ITEM_HELP_ARTICLE_URL,
								target: '_blank',
							},
						],
					});

					// Only fire analytics event if we are not displaying the confirmation modal
					onFireAnalyticsEvent();
				}
				return;
			}

			// Unhandled error scenarios
			onFail();
			onFireAnalyticsEvent();
		},
		[formatMessage, showFlag],
	);

	const updater = useCallback(
		({ store, projectId, mutation, value }: UpdaterProps) => {
			const projectDataId = RelayDataID({ id: projectId }, 'TownsquareProject');
			const fieldDataId = RelayDataID({ id: uniqueFieldId }, 'JiraTownsquareProjectField');
			if (!projectDataId || !fieldDataId) {
				return;
			}
			const fieldRecord = store.get(fieldDataId);
			if (!fieldRecord) {
				return;
			}

			if (mutation === 'link' && value) {
				let projectRecord = store.get(projectDataId);
				if (!projectRecord) {
					projectRecord = store.create(projectDataId, 'TownsquareProject');
				}
				projectRecord.setValue(projectId, 'id');
				projectRecord.setValue(value.name, 'name');
				projectRecord.setValue(value.key, 'key');

				fieldRecord.setLinkedRecord(projectRecord, 'project');
			} else {
				fieldRecord.setValue(null, 'project');
			}
		},
		[uniqueFieldId],
	);

	const handleSubmit = useCallback(
		(value: LinkedProject | null, { onSuccess, onFail }: OnSubmitCallbacks) => {
			// Early return if no change
			if (isEqualOption(value, initialValue)) {
				return;
			}

			const issueId = issue?.id;
			// If linking, the value.id will be the projectId being linked.
			// If unlinking, the value will be null so we fall back to the currently linked project id.
			const projectId = value?.id || project?.id;
			const shouldReplaceChildLinks = replaceChildLinks.current;

			// Reset replaceChildLinks ref
			replaceChildLinks.current = false;

			// Early return if we don't have the required IDs to call the mutation
			if (!issueId || !projectId) {
				onFail();
				return;
			}

			onSubmit?.(value);

			if (value === null) {
				commitUnlinkMutation({
					variables: {
						input: {
							workItemId: issueId,
							projectId,
						},
					},
					onCompleted: (response, graphqlErrors) => {
						if (graphqlErrors && graphqlErrors.length > 0) {
							fireAtlasProjectAnalyticsEvent({
								event: 'operational',
								action: 'unlinked-failed',
								analyticsAttributes: {
									error: graphqlErrors?.[0],
									linkedWorkItemId: issueId,
									projectId,
								},
							});
							onFail();
							return;
						}
						const mutationResponse = response.projects_removeJiraWorkItemLink;

						if (mutationResponse?.success) {
							fireAtlasProjectAnalyticsEvent({
								event: 'track',
								action: 'unlinked',
								analyticsAttributes: {
									linkedWorkItemId: issueId,
									projectId,
								},
							});
							onSuccess();
							return;
						}

						const { errorType, statusCode } = mutationResponse?.errors?.[0]?.extensions || {};
						const errorMessage = mutationResponse?.errors?.[0]?.message || '';

						fireAtlasProjectAnalyticsEvent({
							event: 'track',
							action: 'unlinked-failed',
							analyticsAttributes: {
								error: {
									message: errorMessage,
									errorType,
									statusCode,
								},
								projectId,
							},
						});
						onFail();
					},
					onError: (error: Error) => {
						fireAtlasProjectAnalyticsEvent({
							event: 'operational',
							action: 'unlinked-failed',
							analyticsAttributes: {
								error: {
									message: error.message,
									stack: error.stack,
								},
								linkedWorkItemId: issueId,
								projectId,
							},
						});
						onFail(error);
					},
					optimisticUpdater: (store) => {
						updater({ store, projectId, value, mutation: 'unlink' });
					},
					updater: (store, response) => {
						// Do not update the store if the mutation was not successful
						if (!response?.projects_removeJiraWorkItemLink?.success) {
							return;
						}
						updater({ store, projectId, value, mutation: 'unlink' });
					},
				});
			} else {
				commitLinkMutation({
					variables: {
						input: {
							workItemId: issueId,
							projectId,
							// This should always be true, as on Jira, users should always be able to replace the project attached to a work item
							replaceCurrentWorkItemLink: true,
							replaceChildWorkItemLinks: shouldReplaceChildLinks,
						},
					},
					onCompleted: (response, graphqlErrors) => {
						if (graphqlErrors && graphqlErrors.length > 0) {
							fireAtlasProjectAnalyticsEvent({
								event: 'operational',
								action: 'linked-failed',
								analyticsAttributes: {
									error: graphqlErrors?.[0],
									linkedWorkItemId: issueId,
									projectId,
								},
							});
							onFail();
							return;
						}

						const mutationResponse = response.projects_addJiraWorkItemLink;

						if (mutationResponse?.success) {
							onSuccess();
							fireAtlasProjectAnalyticsEvent({
								event: 'track',
								action: 'linked',
								analyticsAttributes: {
									linkedWorkItemId: issueId,
									projectId,
								},
							});
							return;
						}

						const { errorType, statusCode, canReplace } =
							mutationResponse?.errors?.[0]?.extensions || {};
						const errorMessage = mutationResponse?.errors?.[0]?.message || '';

						handleMutationError({
							errorType,
							canReplace,
							onFail,
							onFireAnalyticsEvent: () => {
								fireAtlasProjectAnalyticsEvent({
									event: 'track',
									action: 'linked-failed',
									analyticsAttributes: {
										error: {
											message: errorMessage,
											errorType,
											statusCode,
										},
										projectId,
									},
								});
							},
						});
					},
					onError: (error: Error) => {
						fireAtlasProjectAnalyticsEvent({
							event: 'operational',
							action: 'linked-failed',
							analyticsAttributes: {
								error: {
									message: error.message,
									stack: error.stack,
								},
								linkedWorkItemId: issueId,
								projectId,
							},
						});
						onFail(error);
					},
					optimisticUpdater: (store) => {
						updater({ store, projectId, value, mutation: 'link' });
					},
					updater: (store, response) => {
						// Do not update the store if the mutation was not successful
						if (!response?.projects_addJiraWorkItemLink?.success) {
							return;
						}

						updater({ store, projectId, value, mutation: 'link' });
					},
				});
			}
		},
		[
			initialValue,
			issue?.id,
			project?.id,
			onSubmit,
			commitUnlinkMutation,
			fireAtlasProjectAnalyticsEvent,
			updater,
			commitLinkMutation,
			handleMutationError,
		],
	);
	// #endregion Mutation handlers

	const {
		invalidMessage,
		isEditing,
		hasServerValidationError,
		handleCancel,
		handleEdit,
		handleConfirm,
		handleChangeAndConfirm,
	} = useFieldInlineEditActions({
		attributes,
		fieldId: uniqueFieldId,
		fieldName,
		fieldType,
		initialValue,
		isValueEqual: isEqualOption,
		onSubmit: handleSubmit,
		onSubmitFailed: () => {
			// Reset the updated value to the initial value on submit failure to allow the user to make another selection
			setUpdatedValue(initialValue);
			onSubmitFailed?.();
		},
		onSubmitSucceeded,
		onUpdateValue: setUpdatedValue,
		startWithEditViewOpen,
		updatedValue,
	});

	// #region Read view
	const renderReadView = () => (
		<MaybeUFOSegment>
			<AtlasProjectReadView fragmentRef={data} onHandleNoAccess={handleNoAccess} />
		</MaybeUFOSegment>
	);
	// #endregion

	// #region Edit view
	const { data: orgId } = useOrgId();
	const cloudId = useCloudId();
	const editViewEntryPointParams = useMemo(() => ({ cloudId }), [cloudId]);

	const getEditViewProps = (fieldProps: ValidationFieldProps): ProjectPickerWithEntrypointProps => {
		return {
			...fieldProps,
			onClose: () => {
				// This is needed as the project picker needs to stay open even when the edit view loses the focus, which is enabled via keepEditViewOpenOnBlur
				// This is to allow users to interact with the project create form, which will be rendered within the project picker
				// Copied from src/packages/platform/ui/inline-edit-lite/src/ui/use-inline-edit/index.tsx
				handleCancel(
					createAnalyticsEvent({
						action: 'canceled',
						actionSubject: 'inlineEdit',
					}),
				);
			},
			onProjectSelect: handleChangeAndConfirm,
			onProjectUnlink: () => handleChangeAndConfirm(null),
			linkedProject: initialValue || undefined,
			isCompact: spacing === 'compact',
			popupProps: {
				placement: popupPlacement,
			},
			cloudId,
			orgId,
			product: 'jira',
			emptyView: <AtlasProjectEmptytView />,
			restrictedView: isRestricted ? <RestrictedAlikeSmartLink popupEditView /> : null,
		};
	};
	// #endregion

	return (
		<>
			<FieldInlineEditLiteWithEntryPoint
				editViewPopup={editViewPopup}
				editViewPopupAlignBlock={editViewPopupAlignBlock}
				editViewEntryPoint={AtlasProjectEditViewEntrypoint}
				editViewEntryPointParams={editViewEntryPointParams}
				getEditViewProps={getEditViewProps}
				fieldName={fieldName}
				hasUnsubmittedChanges={hasServerValidationError}
				invalidMessage={invalidMessage}
				isEditing={isEditing}
				isEditable={isFieldEditable}
				onCancel={handleCancel}
				// Not invoking handleConfirm here as the value updates are handled via onProjectSelect in getEditViewProps and confirmed with handleChangeAndConfirm
				// This is to ensure that Form submit events from the project picker do not interfere with the inline edit confirm flow
				onConfirm={noop}
				onEdit={handleEdit}
				readViewFitContainerHeight={readViewFitContainerHeight}
				readView={renderReadView}
				hideActionButtons
				keepEditViewOpenOnBlur
			/>
			<ModalTransition>
				{showConfirmatonModal && (
					<LinkConfirmationModal
						onClose={() => setShowConfirmationModal(false)}
						onLink={() => {
							replaceChildLinks.current = true;
							handleConfirm(
								createAnalyticsEvent({
									action: 'confirmed',
									actionSubject: 'inlineEdit',
								}),
							);
							setShowConfirmationModal(false);
						}}
					/>
				)}
			</ModalTransition>
		</>
	);
};

/**
 * Inline edit will handle the switching behaviour between the 'readView' and 'editView' components.
 *
 * @param props [AtlasProjectInlineEditViewProps](./types.tsx)
 */
export const AtlasProjectInlineEditView = ({
	fragmentRef,
	...props
}: AtlasProjectInlineEditViewProps): React.JSX.Element => {
	// #region Relay
	const data = useFragment<AtlasProjectFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment atlasProject_issueFieldAtlasProjectInlineEditFull_AtlasProjectInlineEditView_fragmentRef on JiraTownsquareProjectField {
				...atlasProject_issueFieldAtlasProjectInlineEditFull_AtlasProjectInlineEditViewWithIsEditable_fragmentRef
				fieldConfig {
					isEditable
				}
			}
		`,
		fragmentRef,
	);
	// #endregion

	return (
		<AtlasProjectInlineEditViewWithIsEditable
			{...props}
			fragmentRef={data}
			isEditable={data.fieldConfig?.isEditable ?? false}
		/>
	);
};
